/**
 * Identity Configuration for @workspace/identity-react
 * 
 * This configuration is used for standalone authentication outside of Evo native tabs.
 * When running inside an Evo native tab, the app uses useWidgetIdentity from @workspace/utils-react instead.
 */

/**
 * Get the Identity authority URL based on environment
 */
const getIdentityAuthority = (): string => {
  const environment = process.env.REACT_APP_ENVIRONMENT || 'development';
  
  switch (environment) {
    case 'production':
      return 'https://identity.accessacloud.com';
    case 'preprod':
    case 'qa':
      return 'https://preprodidentity.accessacloud.com';
    default:
      // Development uses preprod identity
      return 'https://preprodidentity.accessacloud.com';
  }
};

/**
 * Get the Workspace URL based on environment
 */
const getWorkspaceUrl = (): string => {
  const environment = process.env.REACT_APP_ENVIRONMENT || 'development';
  
  switch (environment) {
    case 'production':
      return 'https://go.accessacloud.com';
    case 'preprod':
    case 'qa':
      return 'https://preprodgo.accessacloud.com';
    default:
      // Development uses preprod workspace
      return 'https://preprodgo.accessacloud.com';
  }
};

/**
 * Get the Identity client ID based on environment
 * - Production/Preprod: access.feedbackdashboard
 * - Dev: access.feedbackdashboard.dev
 */
const getClientId = (): string => {
  const environment = process.env.REACT_APP_ENVIRONMENT || 'development';
  
  switch (environment) {
    case 'production':
      return 'access.feedbackdashboard';
    default:
      // Development use the dev client
      return 'access.feedbackdashboard';
  }
};

/**
 * Identity client configuration
 * 
 * This client is registered for the Feedback Dashboard application.
 * - Local development: http://localhost:3000
 * - Preprod: feedback-widget-preprod.accessacloud.com
 * - Production: feedback-widget.accessacloud.com (requires separate production client registration)
 */
export const identityConfig = {
  /**
   * The Identity authority URL for authentication
   */
  authority: getIdentityAuthority(),
  
  /**
   * The registered client ID for the Feedback Dashboard application.
   * Registered via SelfService Portal with PKCE enabled, client-side logout mode.
   * - Production/Preprod: access.feedbackdashboard
   * - Dev: access.feedbackdashboard.dev
   */
  clientId: getClientId(),
  
  /**
   * The redirect URI for OIDC callback
   */
  redirectUri: `${window.location.origin}/login/callback`,
  
  /**
   * The scopes required for authentication
   * - openid: Required for OIDC
   * - email: Access to user's email
   * - profile: Access to user's profile information
   * - policy: Access to policy information
   */
  scope: 'openid email profile policy',
  
  /**
   * The Workspace URL for iframe communication
   */
  workspaceUrl: getWorkspaceUrl(),
  
  /**
   * Use interactive signin flow for standalone access
   * This allows page redirects to Identity to handle signin
   */
  signinFlow: 'interactive' as const,
};

export type IdentityConfig = typeof identityConfig;
